import { resolveApiBase } from './config'

const injectedFor = new Set<string>()

export interface AnalyticsBootstrapOptions {
  /** Vendor hash. Used to build the gtm-snippet + event-handler endpoint URLs. */
  vendorHash: string
  /** Explicit API base URL. Overrides `env`. */
  apiBase?: string
  /** Evaro environment. Resolves the API base when `apiBase` isn't given. */
  env?: string
}

/**
 * Bootstrap analytics by loading the existing Evaro API scripts:
 *
 *   GET /json/api/v1/vendor/{hash}/gtm-snippet
 *   GET /json/api/v1/vendor/{hash}/event-handler
 *
 * Both endpoints are server-resolved. They return the GTM bootstrap and the
 * event-handler bridge with the vendor's `google_tag_manager_id` ACF field
 * baked in. When the field is empty the endpoints return empty strings, so
 * loading them is safe whether or not the vendor has analytics configured.
 *
 * The Evaro API is the source of truth for all per-vendor analytics config.
 * The SDK does not duplicate that logic; it just loads what the API serves.
 *
 * Idempotent per vendor — calling twice is a no-op so it's safe to invoke
 * from both the loader and a React provider.
 */
export function bootstrapAnalytics({ vendorHash, apiBase, env }: AnalyticsBootstrapOptions): void {
  if (typeof window === 'undefined') return
  if (!vendorHash) return

  if (injectedFor.has(vendorHash)) return
  injectedFor.add(vendorHash)

  const base = resolveApiBase({ apiBase, env }).replace(/\/$/, '')
  inject(`${base}/json/api/v1/vendor/${encodeURIComponent(vendorHash)}/gtm-snippet`, {
    defer: false,
    attribute: 'data-evaro-gtm',
  })
  inject(`${base}/json/api/v1/vendor/${encodeURIComponent(vendorHash)}/event-handler`, {
    defer: true,
    attribute: 'data-evaro-event-handler',
  })
}

function inject(src: string, opts: { defer: boolean; attribute: string }): void {
  if (document.querySelector(`script[${opts.attribute}="${src}"]`)) return
  const s = document.createElement('script')
  s.src = src
  if (opts.defer) s.defer = true
  s.setAttribute(opts.attribute, src)
  ;(document.head || document.body).appendChild(s)
}

/** Visible for tests. */
export function __resetAnalyticsBridgeForTests(): void {
  injectedFor.clear()
}
